/**
 * The Sales-dashboard (tenant-admin) session cookies. No imports, so the
 * Edge middleware, the API routes and the browser all share it.
 *
 * - TOKEN_COOKIE: the signed Studio token. HttpOnly, so page scripts (and
 *   any XSS) never see it; Path=/api/studio, so only the proxies receive it.
 *   middleware.ts turns it into the `Authorization: Bearer` header the
 *   proxies forward to Studio.
 * - EXPIRY_COOKIE: when that token expires (epoch ms). Not a secret; page
 *   scripts read it to know a session exists and to sign out on time.
 *
 * Both carry the token's own TTL, so the browser drops them when Studio
 * would start refusing the token anyway.
 */

export const TENANT_ADMIN_TOKEN_COOKIE = 'studio_tenant_admin_token'
export const TENANT_ADMIN_EXPIRY_COOKIE = 'studio_tenant_admin_expires'
const TOKEN_PATH = '/api/studio'

/** Studio's default TENANT_ADMIN_TOKEN_TTL_SECONDS, for a Studio that does
 * not send `expires_in` yet. */
const DEFAULT_TTL_SECONDS = 24 * 60 * 60

function cookie(
	name: string,
	value: string,
	maxAge: number,
	path: string,
	httpOnly: boolean
): string {
	return [
		`${name}=${encodeURIComponent(value)}`,
		`Path=${path}`,
		`Max-Age=${maxAge}`,
		'SameSite=Lax',
		httpOnly ? 'HttpOnly' : '',
		process.env.NODE_ENV === 'production' ? 'Secure' : '',
	]
		.filter(Boolean)
		.join('; ')
}

/** Set-Cookie values that start a session on `token`. */
export function tenantAdminSessionCookies(
	token: string,
	expiresIn: unknown,
	now: number = Date.now()
): { cookies: string[]; expiresAt: number } {
	const ttl =
		typeof expiresIn === 'number' && expiresIn > 0
			? Math.floor(expiresIn)
			: DEFAULT_TTL_SECONDS
	const expiresAt = now + ttl * 1000
	return {
		expiresAt,
		cookies: [
			cookie(TENANT_ADMIN_TOKEN_COOKIE, token, ttl, TOKEN_PATH, true),
			cookie(TENANT_ADMIN_EXPIRY_COOKIE, String(expiresAt), ttl, '/', false),
		],
	}
}

/** Set-Cookie values that end the session. */
export function clearedTenantAdminSessionCookies(): string[] {
	return [
		cookie(TENANT_ADMIN_TOKEN_COOKIE, '', 0, TOKEN_PATH, true),
		cookie(TENANT_ADMIN_EXPIRY_COOKIE, '', 0, '/', false),
	]
}
